// Convention: component-element or component-element-{dynamic}
export const testIds = {
  // Desktop
  desktop: {
    root: 'desktop-root',
    iconGrid: 'desktop-icon-grid',
    icon: (path: string) => `desktop-icon-${path.replace(/\//g, '')}`,
  },
  // Window chrome
  window: {
    frame: (path: string) => `window-frame-${path.replace(/\//g, '')}`,
    titleBar: (path: string) => `window-titlebar-${path.replace(/\//g, '')}`,
    minimize: (path: string) => `window-minimize-${path.replace(/\//g, '')}`,
    maximize: (path: string) => `window-maximize-${path.replace(/\//g, '')}`,
    close: (path: string) => `window-close-${path.replace(/\//g, '')}`,
    content: (path: string) => `window-content-${path.replace(/\//g, '')}`,
    resizeHandle: (path: string) => `window-resize-${path.replace(/\//g, '')}`,
  },
  // Taskbar
  taskbar: {
    root: 'taskbar-root',
    startButton: 'taskbar-start-button',
    clock: 'taskbar-clock',
    windowButton: (path: string) => `taskbar-window-${path.replace(/\//g, '')}`,
  },
  // Phase E — Win95 status bar (5 cells: Convex / M365 / Portal / Index / User)
  statusBar: {
    root: 'status-bar-root',
    cellConvex: 'status-bar-cell-convex',
    cellM365: 'status-bar-cell-m365',
    cellPortal: 'status-bar-cell-portal',
    cellIndex: 'status-bar-cell-index',
    cellUser: 'status-bar-cell-user',
    userMenu: 'status-bar-user-menu',
    signOutMenuItem: 'status-bar-sign-out',
    // K.3.b — M365 cell tooltip details + "View in SharePoint" link
    // rendered when the active portal resolves to a real SP site.
    cellM365TenantBadge: 'status-bar-cell-m365-tenant-badge',
    cellM365TenantSiteId: 'status-bar-cell-m365-tenant-site-id',
    cellM365TenantDriveId: 'status-bar-cell-m365-tenant-drive-id',
    cellM365TenantOpenLink: 'status-bar-cell-m365-tenant-open-link',
  },
  // Phase E — per-doc provenance footer (mounted inside DocumentPreview)
  documentProvenance: {
    root: 'document-provenance-root',
    source: 'document-provenance-source',
    lastModified: 'document-provenance-last-modified',
    author: 'document-provenance-author',
    liveBadge: 'document-provenance-live-badge',
    webUrl: 'document-provenance-web-url',
  },
  // Start menu
  startMenu: {
    root: 'start-menu-root',
    item: (label: string) => `start-menu-item-${label.toLowerCase().replace(/\s+/g, '-')}`,
    signOut: 'start-menu-sign-out',
    themeToggle: 'start-menu-theme-toggle',
  },
  // SharePoint Explorer
  explorer: {
    root: 'explorer-root',
    toolbar: 'explorer-toolbar',
    addressBar: 'explorer-address-bar',
    treePane: 'explorer-tree-pane',
    listPane: 'explorer-list-pane',
    previewPane: 'explorer-preview-pane',
    treeDivider: 'explorer-tree-divider',
    previewDivider: 'explorer-preview-divider',
    commonTasks: 'explorer-common-tasks',
    treeNode: (type: string, id: string) => `tree-node-${type}-${id}`,
    treeExpand: (id: string) => `tree-expand-${id}`,
    // Document list
    columnHeader: (name: string) => `column-header-${name.toLowerCase()}`,
    filterInput: (name: string) => `filter-${name.toLowerCase()}`,
    docRow: (id: string) => `doc-row-${id}`,
    sortIndicator: (name: string) => `sort-indicator-${name.toLowerCase()}`,
    // Preview
    previewTitle: 'preview-title',
    previewContent: 'preview-content',
    // Phase D — footnote drawer that opens when a docx sidecar
    // footnote marker is clicked inside the Tiptap editor.
    previewFootnoteDrawer: 'preview-footnote-drawer',
    sendToPromptLab: 'preview-send-to-prompt-lab',
    versionHistory: 'preview-version-history',
    // K.3.a — proof-of-SharePoint "Open in SharePoint" button (preview +
    // per-row icon). Real webUrls render an active anchor; fixture docs
    // get a disabled, muted button so the absence is intentional.
    openInSharePoint: 'document-preview-open-in-sharepoint',
    openInSharePointBadge: 'document-preview-open-in-sharepoint-badge',
    openInSharePointExplain: 'document-preview-open-in-sharepoint-explain',
    openInSharePointExplainer: 'document-preview-open-in-sharepoint-explainer',
    // Phase 6.5 — Public share-link affordance on DocumentPreview.
    shareDocPublicly: 'document-preview-share-publicly',
    shareDocResultPanel: 'document-preview-share-publicly-result',
    shareDocUrlInput: 'document-preview-share-publicly-url',
    shareDocCopyButton: 'document-preview-share-publicly-copy',
    shareDocOpenLink: 'document-preview-share-publicly-open',
    shareDocErrorPanel: 'document-preview-share-publicly-error',
    // Phase 6.5 — Per-matter public share-link affordance on DocumentList.
    shareMatterPublicly: 'document-list-share-matter-publicly',
    shareMatterResultPanel: 'document-list-share-matter-result',
    shareMatterUrlInput: 'document-list-share-matter-url',
    shareMatterCopyButton: 'document-list-share-matter-copy',
    shareMatterOpenLink: 'document-list-share-matter-open',
    shareMatterErrorPanel: 'document-list-share-matter-error',
    docRowOpenInSharePoint: (id: string) =>
      `doc-row-open-in-sharepoint-${id}`,
    // Toolbar buttons
    backButton: 'explorer-back',
    forwardButton: 'explorer-forward',
    upButton: 'explorer-up',
    // Demo / live workspace toggle
    demoToggle: 'explorer-demo-toggle',
    demoToggleInput: 'explorer-demo-toggle-input',
    // F10 two-group rendering
    treeGroup: (group: string) => `explorer-tree-group-${group}`,
    treeGroupHeader: (group: string) => `explorer-tree-group-header-${group}`,
    treeGroupConnectCta: (group: string) => `explorer-tree-group-connect-${group}`,
    // K.8 — RemoteDriveItemPreview surfaces (sibling of DocumentPreview
    // for connected-site driveItems).
    remoteDriveItemRoot: 'explorer-remote-driveitem-root',
    remoteDriveItemLoading: 'explorer-remote-driveitem-loading',
    remoteDriveItemError: 'explorer-remote-driveitem-error',
    remoteDriveItemBinary: 'explorer-remote-driveitem-binary',
    remoteDriveItemRendered: 'explorer-remote-driveitem-rendered',
    remoteDriveItemMarkdown: 'explorer-remote-driveitem-markdown',
    remoteDriveItemText: 'explorer-remote-driveitem-text',
    remoteDriveItemViewSwitcher: 'explorer-remote-driveitem-view-switcher',
    remoteDriveItemViewButton: (view: string) =>
      `explorer-remote-driveitem-view-${view}`,
    remoteDriveItemOpenInSharePoint: 'explorer-remote-driveitem-open-in-sharepoint',
    // K.7 — "Your connected sites" group (Sites.Selected per-site grants)
    connectedSiteEmptyState: 'explorer-connected-sites-empty',
    connectedSiteNode: (siteId: string) =>
      `connected-site-node-${siteId.replace(/[^a-zA-Z0-9]/g, '-').slice(0, 40)}`,
    connectedSiteExpand: (siteId: string) =>
      `connected-site-expand-${siteId.replace(/[^a-zA-Z0-9]/g, '-').slice(0, 40)}`,
    connectedSiteDriveNode: (driveId: string) =>
      `connected-site-drive-${driveId.replace(/[^a-zA-Z0-9]/g, '-').slice(0, 40)}`,
    connectedSiteChildNode: (childId: string) =>
      `connected-site-child-${childId.replace(/[^a-zA-Z0-9]/g, '-').slice(0, 40)}`,
    connectedSiteLoading: (siteId: string) =>
      `connected-site-loading-${siteId.replace(/[^a-zA-Z0-9]/g, '-').slice(0, 40)}`,
    connectedSiteError: (siteId: string) =>
      `connected-site-error-${siteId.replace(/[^a-zA-Z0-9]/g, '-').slice(0, 40)}`,
    // K.9 — folder drill-down inside connected sites
    connectedSiteFolderExpand: (folderId: string) =>
      `connected-folder-expand-${folderId.replace(/[^a-zA-Z0-9]/g, '-').slice(0, 40)}`,
    connectedSiteFolderLoading: (folderId: string) =>
      `connected-folder-loading-${folderId.replace(/[^a-zA-Z0-9]/g, '-').slice(0, 40)}`,
    connectedSiteFolderError: (folderId: string) =>
      `connected-folder-error-${folderId.replace(/[^a-zA-Z0-9]/g, '-').slice(0, 40)}`,
    // K.11.3 — RemoteDriveItemPreview read-only banner + inline upgrade CTA
    remoteDriveItemReadOnlyBanner: 'explorer-remote-driveitem-readonly-banner',
    remoteDriveItemUpgradeInlineButton: 'explorer-remote-driveitem-upgrade-inline',
    remoteDriveItemPermissionBadge: 'explorer-remote-driveitem-permission-badge',
    // K.12 — md/text editing + save-back surfaces
    remoteDriveItemEditor: 'explorer-remote-driveitem-editor',
    remoteDriveItemSaveButton: 'explorer-remote-driveitem-save',
    remoteDriveItemSaveErrorPanel: 'explorer-remote-driveitem-save-error',
    remoteDriveItemSaveSuccessMessage: 'explorer-remote-driveitem-save-success',
    remoteDriveItemDiscardButton: 'explorer-remote-driveitem-discard',
    // F-DOCX-EDIT — surfaces the connected-sites docx lossy-save warning
    // before the user clicks Save. Auto-provisioned portals don't show
    // this badge because they round-trip OOXML server-side.
    remoteDriveItemDocxLossyBadge: 'explorer-remote-driveitem-docx-lossy',
    // K.9.8 — RemoteFolderPreview (right pane when a folder ROW (not toggle) is clicked)
    remoteFolderRoot: 'explorer-remote-folder-root',
    remoteFolderTitle: 'explorer-remote-folder-title',
    remoteFolderChildCount: 'explorer-remote-folder-child-count',
    remoteFolderOpenInSharePoint: 'explorer-remote-folder-open-in-sharepoint',
    remoteFolderLoading: 'explorer-remote-folder-loading',
    remoteFolderError: 'explorer-remote-folder-error',
  },
  // Client List
  clientList: {
    root: 'client-list-root',
    row: (id: string) => `client-row-${id}`,
    columnHeader: (name: string) => `client-col-${name.toLowerCase()}`,
    emptyState: 'client-list-empty',
  },
  // Prompt Lab
  promptLab: {
    root: 'prompt-lab-root',
    toolbar: 'prompt-lab-toolbar',
    systemPrompt: 'system-prompt',
    systemPromptToggle: 'system-prompt-toggle',
    systemPromptSummary: 'system-prompt-summary',
    userPrompt: 'user-prompt',
    runButton: 'run-prompt-button',
    stopButton: 'stop-prompt-button',
    statusBar: 'prompt-lab-status-bar',
    // Model selector
    modelSelector: 'model-selector',
    modelDropdown: 'model-dropdown',
    modelOption: (id: string) => `model-option-${id}`,
    modelFavorite: (id: string) => `model-favorite-${id}`,
    modelProviderGroup: (provider: string) => `model-provider-${provider.toLowerCase()}`,
    favoritesToggle: 'model-favorites-toggle',
    // L.11 — Specialization selector
    specializationSelect: 'prompt-lab-specialization-select',
    specializationLoading: 'prompt-lab-specialization-loading',
    specializationEmpty: 'prompt-lab-specialization-empty',
    specializationBanner: 'prompt-lab-specialization-banner',
    // D2 — live inference job state surfaced inline.
    inferenceStatus: 'prompt-lab-inference-status',
    inferenceError: 'prompt-lab-inference-error',
    // TL — Timeline panel embedded inside PromptLab (replaces /timeline window).
    timelinePanel: 'prompt-lab-timeline-panel',
    timelineToggle: 'prompt-lab-timeline-toggle',
    timelineForkSelect: 'prompt-lab-timeline-fork-select',
    timelineTabForks: 'prompt-lab-timeline-tab-forks',
    timelineTabDiff: 'prompt-lab-timeline-tab-diff',
    // Skill prompts
    skillSlots: 'skill-slots',
    addSkillButton: 'add-skill-button',
    skillCard: (id: string) => `skill-card-${id}`,
    skillEnable: (id: string) => `skill-enable-${id}`,
    skillWeight: (id: string) => `skill-weight-${id}`,
    skillRemove: (id: string) => `skill-remove-${id}`,
    skillExpand: (id: string) => `skill-expand-${id}`,
    skillContent: (id: string) => `skill-content-${id}`,
    skillVersionChip: (id: string) => `skill-version-chip-${id}`,
    skillDisconnect: (id: string) => `skill-disconnect-${id}`,
    // Step cards
    stepCard: (id: string) => `step-card-${id}`,
    stepFork: (id: string) => `step-fork-${id}`,
    stepToggle: (id: string) => `step-toggle-${id}`,
    // Output
    outputArea: 'output-area',
    generating: 'generating-indicator',
    // Document context
    docContext: 'document-context',
    docContextItem: (id: string) => `doc-context-${id}`,
    docContextRemove: (id: string) => `doc-context-remove-${id}`,
    docContextDropZone: 'doc-context-drop-zone',
    // Toolbar buttons
    newSession: 'prompt-lab-new-session',
    clearSession: 'prompt-lab-clear',
    saveOutput: 'prompt-lab-save-output',
    // Active-client picker (D5)
    activeClientPicker: 'prompt-lab-active-client-picker',
    activeClientSelect: 'prompt-lab-active-client-select',
    activeClientProfileChip: 'prompt-lab-active-client-profile-chip',
    activeClientNoProfileChip: 'prompt-lab-active-client-no-profile-chip',
    activeClientProfileLoadedChip: 'prompt-lab-active-client-profile-loaded-chip',
  },
  // Timeline
  timeline: {
    root: 'timeline-root',
    scrubber: 'timeline-scrubber',
    scrubberTrack: 'timeline-scrubber-track',
    stepMarker: (index: number) => `step-marker-${index}`,
    highWaterMark: 'timeline-high-water',
    stepLabel: 'timeline-step-label',
    // Diff
    diffToggle: 'diff-toggle',
    diffSplit: 'diff-split-button',
    diffUnified: 'diff-unified-button',
    sideBySide: 'diff-side-by-side',
    unified: 'diff-unified',
    diffOriginal: 'diff-original-column',
    diffForked: 'diff-forked-column',
    // Fork controls
    forkControls: 'fork-controls',
    forkHere: 'fork-here-button',
    forkCard: (id: string) => `fork-card-${id}`,
    forkViewDiff: (id: string) => `fork-view-diff-${id}`,
    forkResume: (id: string) => `fork-resume-${id}`,
    forkDelete: (id: string) => `fork-delete-${id}`,
    forkLabelInput: 'fork-label-input',
  },
  // Settings
  settings: {
    root: 'settings-root',
    tab: (name: string) => `settings-tab-${name.toLowerCase()}`,
    // T.0 Tinker tab
    tinkerPanel: 'settings-tinker-panel',
    tinkerStatusBadge: 'settings-tinker-status',
    tinkerBaseModelInput: 'settings-tinker-base-model',
    tinkerLoraRankInput: 'settings-tinker-lora-rank',
    tinkerEpochsInput: 'settings-tinker-epochs',
    tinkerLearningRateInput: 'settings-tinker-learning-rate',
    tinkerRunHistoryRow: (id: string) => `settings-tinker-run-${id}`,
    tinkerLastLossDisplay: 'settings-tinker-last-loss',
    tinkerLastCostDisplay: 'settings-tinker-last-cost',
    tinkerEvalOutputs: 'settings-tinker-eval-outputs',
    tinkerBillingHelpLink: 'settings-tinker-billing-help',
    // T.1 — editable training-settings form
    tinkerSettingsForm: 'settings-tinker-form',
    tinkerSaveButton: 'settings-tinker-save',
    tinkerResetButton: 'settings-tinker-reset',
    tinkerRetrainButton: 'settings-tinker-retrain',
    tinkerCostCapInput: 'settings-tinker-cost-cap',
    tinkerDatasetScopeOption: (key: string) =>
      `settings-tinker-dataset-scope-${key}`,
    tinkerSaveStatus: 'settings-tinker-save-status',
    tinkerSkillsUsed: 'settings-tinker-skills-used',
    // T.2.a.3 — orchestrated retrain (Convex action → Function App)
    tinkerJobStatusBanner: 'settings-tinker-job-status-banner',
    tinkerRetrainStatus: 'settings-tinker-retrain-status',
    tinkerJobErrorBanner: 'settings-tinker-job-error',
    themeToggle: 'settings-theme-toggle',
    apiKeyInput: 'input-api-key',
    apiKeyShow: 'api-key-show-toggle',
    apiKeyTest: 'api-key-test-button',
    apiKeyStatus: 'api-key-status',
    apiKeyOpenRouterLink: 'api-key-openrouter-link',
    resetData: 'settings-reset-data',
    okButton: 'settings-ok',
    cancelButton: 'settings-cancel',
    brandCredit: 'settings-brand-credit',
    // applyButton removed in phase-J dead-button sweep — see decisions.md.
    // Each Settings tab saves its own state on its own buttons; OK and
    // Cancel both close the window.
    // Phase 6 — Members tab (email-invite via Graph /invitations).
    membersPanel: 'settings-members-panel',
    membersInviteButton: 'settings-members-invite',
    membersEmailInput: 'settings-members-email-input',
    membersDisplayNameInput: 'settings-members-displayname-input',
    membersRoleSelect: 'settings-members-role-select',
    membersSendEmailCheckbox: 'settings-members-send-email-checkbox',
    membersSubmitButton: 'settings-members-submit',
    membersInviteStatus: 'settings-members-invite-status',
    membersRow: (id: string) => `settings-members-row-${id}`,
    // Phase 7 — Public-sharing two-mode toggle on the Members tab.
    membersSharingTogglePublic: 'settings-members-sharing-public',
    membersSharingToggleDisabled: 'settings-members-sharing-disabled',
    membersSharingStatus: 'settings-members-sharing-status',
    diagnosticsTab: 'settings-diagnostics-root',
    diagnosticsRunGraph: 'settings-diagnostics-run-graph',
    diagnosticsReseed: 'settings-diagnostics-reseed',
    diagnosticsBackendSmoke: 'settings-diagnostics-backend-smoke',
    diagnosticsBackendSmokeRow: (id: string) =>
      `settings-diagnostics-backend-smoke-row-${id}`,
    demoToggle: 'settings-demo-toggle',
    // Phase E2 — Engineering tab (renders engineeringStatus rows).
    engineeringTab: 'settings-tab-engineering',
    engineeringRoot: 'settings-engineering-root',
    engineeringEmpty: 'settings-engineering-empty',
    engineeringLoading: 'settings-engineering-loading',
    engineeringCard: (trackId: string) =>
      `settings-engineering-card-${trackId.replace(/[^a-zA-Z0-9_-]/g, '_')}`,
    engineeringStatusBadge: (trackId: string) =>
      `settings-engineering-status-${trackId.replace(/[^a-zA-Z0-9_-]/g, '_')}`,
  },
  // H.1 — Schema-profile editor (operator surface).
  schemaProfiles: {
    root: 'schema-profiles-root',
    profileList: 'schema-profiles-list',
    profileRow: (key: string) => `schema-profile-row-${key.replace(/[^a-zA-Z0-9_-]/g, '_')}`,
    profileKeyInput: 'schema-profile-key-input',
    profileSlugInput: 'schema-profile-slug-input',
    profileBodyTextarea: 'schema-profile-body-textarea',
    saveButton: 'schema-profile-save',
    deleteButton: 'schema-profile-delete',
    resetButton: 'schema-profile-reset',
    addNewButton: 'schema-profile-add-new',
    errorBanner: 'schema-profile-error',
  },
  // Trust routes
  trust: {
    root: 'trust-root',
    navLink: (key: string) => `trust-nav-${key}`,
  },
  // Save dialog
  saveDialog: {
    root: 'save-dialog-root',
    newDocRadio: 'save-new-document-radio',
    newVersionRadio: 'save-new-version-radio',
    titleInput: 'save-title-input',
    matterSelect: 'save-matter-select',
    typeSelect: 'save-doc-type-select',
    confidentialitySelect: 'save-confidentiality-select',
    changeDescription: 'save-change-description',
    statusSelect: 'save-status-select',
    saveButton: 'save-dialog-save',
    cancelButton: 'save-dialog-cancel',
    preview: 'save-dialog-preview',
  },
  // B.7.c.2 — Microsoft account / token health
  microsoftAccount: {
    panelRoot: 'ms-account-panel',
    tokenStatusBadge: 'ms-account-token-status-badge',
    reconnectButton: 'ms-account-reconnect-button',
    refreshSuccessToast: 'ms-account-refresh-success-toast',
    // E.4 — Vault outbound status surface
    outboundStatusSection: 'ms-account-outbound-status',
    outboundCountsPending: 'ms-account-outbound-counts-pending',
    outboundCountsInFlight: 'ms-account-outbound-counts-in-flight',
    outboundCountsFailed: 'ms-account-outbound-counts-failed',
    outboundCountsNeedsResolution: 'ms-account-outbound-counts-needs-resolution',
    outboundForceSyncButton: 'ms-account-outbound-force-sync',
  },
  // B.7.e — Portal-creation wizard
  portalWizard: {
    root: 'portal-wizard-root',
    forbidden: 'portal-wizard-forbidden',
    stepIndicator: 'portal-wizard-step-indicator',
    stepNumber: (n: 1 | 2 | 3 | 4) => `portal-wizard-step-${n}`,
    // Step 1 — pick template
    templatePicker: 'portal-wizard-template-picker',
    templateOption: (id: string) => `portal-wizard-template-${id}`,
    targetSlugInput: 'portal-wizard-target-slug-input',
    step1Next: 'portal-wizard-step1-next',
    // Step 2 — upload files
    uploadDropzone: 'portal-wizard-upload-dropzone',
    uploadedFile: (name: string) => `portal-wizard-uploaded-${name}`,
    uploadMatterSelect: (name: string) =>
      `portal-wizard-uploaded-${name}-matter-select`,
    uploadFolderSelect: (name: string) =>
      `portal-wizard-uploaded-${name}-folder-select`,
    step2Next: 'portal-wizard-step2-next',
    step2Back: 'portal-wizard-step2-back',
    // Step 3 — sanitize gate
    sanitizeDiff: (name: string) => `portal-wizard-sanitize-diff-${name}`,
    sanitizeConfirm: (name: string) => `portal-wizard-sanitize-confirm-${name}`,
    sanitizeProgress: 'portal-wizard-sanitize-progress',
    step3Next: 'portal-wizard-step3-next',
    step3Back: 'portal-wizard-step3-back',
    // Step 4 — provision + poll
    provisionStart: 'portal-wizard-provision-start',
    provisionProgress: 'portal-wizard-provision-progress',
    provisionResultUrl: 'portal-wizard-provision-result-url',
    provisionError: 'portal-wizard-provision-error',
    step4Back: 'portal-wizard-step4-back',
  },
  // Auth
  auth: {
    signInPage: 'sign-in-page',
    signInFooter: 'sign-in-footer',
    loadingScreen: 'loading-screen',
    portalDirectory: 'auth-portal-directory',
    portalDirectoryItem: (slug: string) => `auth-portal-directory-${slug}`,
    // B.2.b — role-aware UI affordances
    roleIndicator: 'auth-role-indicator',
    roleSwitcherDevButton: {
      operator: 'auth-role-switcher-operator',
      associate: 'auth-role-switcher-associate',
      clear: 'auth-role-switcher-clear',
    },
  },
  // Phase D / J.3 — Portal provisioning testids.
  //
  // The `onboarding-*` testids originated with Phase D's blocking
  // interstitial. J.2 stripped the interstitial; J.3 re-uses the same
  // testids inside the WorkspaceSetupPanel windowed surface so the
  // smoke harness + Playwright specs don't break. The
  // `workspaceSetup-*` family below adds the J.3-specific surfaces
  // (4 entry points + window-root + open-existing CTA).
  onboarding: {
    interstitial: 'onboarding-interstitial',
    slugInput: 'onboarding-slug-input',
    slugContinueButton: 'onboarding-slug-continue',
    slugErrorMessage: 'onboarding-slug-error',
    progressBubble: (key: string) => `onboarding-bubble-${key}`,
    loaderText: 'onboarding-loader-text',
    retryButton: 'onboarding-retry',
    finalEnterButton: 'onboarding-enter-portal',
  },
  // J.3 — non-blocking WorkspaceSetupPanel + 4 opt-in entry points.
  // K.5 — adds sandboxBadge + msSignInLink for the demo-refocus copy
  // shipped in SlugEditorBody.
  workspaceSetup: {
    root: 'workspace-setup-root',
    openExisting: 'workspace-setup-open-existing',
    statusBubble: 'workspace-setup-status-bubble',
    sandboxBadge: 'workspace-setup-sandbox-badge',
    msSignInLink: 'workspace-setup-ms-sign-in-link',
    // 4 entry-point surfaces. Each id mirrors the factory it lands
    // through:
    //   - startMenuItem mirrors `startMenu.item(label)`: lowercased
    //     space→hyphen for label "Create my private workspace".
    //   - desktopIcon mirrors `desktop.icon(path)`: path with slashes
    //     stripped for "/admin/workspace-setup".
    //   - settingsTab mirrors `settings.tab(key)`: key 'workspace'.
    //   - nipeMenuItem mirrors `nipe.topicTile(key)`: key
    //     'create-private-workspace'.
    startMenuItem: 'start-menu-item-create-my-private-workspace',
    desktopIcon: 'desktop-icon-adminworkspace-setup',
    settingsTab: 'settings-tab-workspace',
    nipeMenuItem: 'nipe-topic-create-private-workspace',
  },
  // K.6 — Sites.Selected per-site SharePoint grant flow. Rendered inside
  // the Settings → Workspace tab under the WorkspaceSetupPanel. Users
  // sign in with Microsoft, list their sites, explicitly grant Repancy
  // read access to specific ones.
  connectMySharePoint: {
    root: 'connect-my-sharepoint-root',
    signInButton: 'connect-my-sharepoint-sign-in',
    discoverButton: 'connect-my-sharepoint-discover',
    connectButton: 'connect-my-sharepoint-connect',
    connectedList: 'connect-my-sharepoint-connected-list',
    errorMessage: 'connect-my-sharepoint-error',
    discoveredRow: (siteId: string): string =>
      `connect-my-sharepoint-discovered-${siteId.replace(/[^a-zA-Z0-9]/g, '-').slice(0, 40)}`,
    connectedRow: (siteId: string): string =>
      `connect-my-sharepoint-connected-${siteId.replace(/[^a-zA-Z0-9]/g, '-').slice(0, 40)}`,
    disconnectButton: (siteId: string): string =>
      `connect-my-sharepoint-disconnect-${siteId.replace(/[^a-zA-Z0-9]/g, '-').slice(0, 40)}`,
    // K.11.2 — per-site upgrade/downgrade buttons
    upgradeButton: (siteId: string): string =>
      `connect-my-sharepoint-upgrade-${siteId.replace(/[^a-zA-Z0-9]/g, '-').slice(0, 40)}`,
    downgradeButton: (siteId: string): string =>
      `connect-my-sharepoint-downgrade-${siteId.replace(/[^a-zA-Z0-9]/g, '-').slice(0, 40)}`,
  },
  // Portal chrome (F16.B / TenantChrome)
  portal: {
    root: 'portal-root',
    titleBar: 'portal-title-bar',
    titleBarDisplayName: 'portal-title-bar-display-name',
    practiceAreaBadge: 'portal-practice-area-badge',
    whatIsThis: 'portal-what-is-this',
    whatIsThisPanel: 'portal-what-is-this-panel',
    signInButton: 'portal-sign-in-button',
    accountChip: 'portal-account-chip',
    signOutButton: 'portal-sign-out',
    signInError: 'portal-sign-in-error',
    logo: 'portal-logo',
    monogram: 'portal-monogram',
    ornament: 'portal-ornament',
    loading: 'portal-loading',
    notFound: 'portal-not-found',
    desktopHost: 'portal-desktop-host',
    signedOut: 'portal-signed-out',
    signedOutResume: 'portal-signed-out-resume',
    // Landing
    landingTile: 'portal-landing-tile',
    landingValueProp: 'portal-landing-value-prop',
    landingSignInPrimary: 'portal-landing-sign-in-primary',
    landingDemoPrimary: 'portal-landing-demo-primary',
    // Skills surface
    skillsRoot: 'portal-skills-root',
    skillsListPane: 'portal-skills-list-pane',
    skillsDetailPane: 'portal-skills-detail-pane',
    skillsListRow: (id: string) => `portal-skills-list-row-${id}`,
    skillsEmpty: 'portal-skills-empty',
    skillsSyncButton: 'portal-skills-sync-button',
    skillsSyncError: 'portal-skills-sync-error',
    skillsBodyPreview: 'portal-skills-body-preview',
    skillsVersionPill: 'portal-skills-version-pill',
    skillsHistoryButton: 'portal-skills-history-button',
    skillsHistoryDrawer: 'portal-skills-history-drawer',
    skillsHistoryClose: 'portal-skills-history-close',
    skillsHistoryVersion: (n: number) => `portal-skills-history-v${n}`,
    // Skill runner
    skillRunnerRoot: 'portal-skill-runner-root',
    skillRunnerClose: 'portal-skill-runner-close',
    skillRunnerDocTitle: 'portal-skill-runner-doc-title',
    skillRunnerPickedSkill: 'portal-skill-runner-picked-skill',
    skillRunnerPromptPreview: 'portal-skill-runner-prompt-preview',
    skillRunnerSend: 'portal-skill-runner-send',
    skillRunnerMenu: 'portal-skill-runner-menu',
    // Stable id for the "Run Skill" entry in the document context menu.
    // One menu, one entry → fixed string, not a factory.
    skillRunnerMenuItem: 'context-menu-run-skill',
    // Save-back surface
    saveBackRoot: 'portal-save-back-root',
    saveBackClose: 'portal-save-back-close',
    saveBackDocTitle: 'portal-save-back-doc-title',
    saveBackEditorPane: 'portal-save-back-editor-pane',
    saveBackPreviewPane: 'portal-save-back-preview-pane',
    saveBackIframe: 'portal-save-back-iframe',
    saveBackEmbedFallback: 'portal-save-back-embed-fallback',
    saveBackNoUrl: 'portal-save-back-no-url',
    saveBackOpenInSharePoint: 'portal-save-back-open-in-sharepoint',
    saveBackStatusBar: 'portal-save-back-status-bar',
  },
  // Admin consent landing (after MSAL /adminconsent redirect)
  adminConsentLanding: {
    banner: 'admin-consent-landing-banner',
    dismiss: 'admin-consent-landing-dismiss',
    tenantName: 'admin-consent-landing-tenant-name',
    deeplink: (slug: string) => `admin-consent-landing-deeplink-${slug}`,
  },
  // Admin → portals dashboard
  adminPortals: {
    root: 'admin-portals-root',
    list: 'admin-portals-list',
    portalRow: (slug: string) => `admin-portals-row-${slug}`,
    subHealth: (slug: string) => `admin-portals-sub-health-${slug}`,
  },
  // Corpus Search Window (D.2.b + Phase E modes)
  corpusSearch: {
    root: 'corpus-search-root',
    queryInput: 'corpus-search-query',
    submit: 'corpus-search-submit',
    notConfigured: 'corpus-search-not-configured',
    error: 'corpus-search-error',
    emptyState: 'corpus-search-empty',
    resultsList: 'corpus-search-results',
    resultRow: (key: string) => `corpus-search-row-${key}`,
    sourcePill: (source: string) => `corpus-search-source-${source}`,
    // Phase E
    modeToggle: 'corpus-search-mode-toggle',
    modeOption: (mode: string) => `corpus-search-mode-${mode}`,
    semanticToggle: 'corpus-search-semantic-toggle',
    embedFailed: 'corpus-search-embed-failed',
  },
  // Feedback thumbs (per-output)
  feedback: {
    thumbsRoot: 'feedback-thumbs-root',
    thumbUp: 'feedback-thumb-up',
    thumbDown: 'feedback-thumb-down',
    noteInput: 'feedback-note-input',
    notePreview: 'feedback-note-preview',
  },
  // SharePoint provenance badges/pills
  spProvenance: {
    badge: 'sp-provenance-badge',
    chromePill: 'sp-provenance-chrome-pill',
    chromePillSiteUrl: 'sp-provenance-chrome-pill-site-url',
    driveId: 'sp-provenance-drive-id',
    etag: 'sp-provenance-etag',
    itemId: 'sp-provenance-item-id',
    openLink: 'sp-provenance-open-link',
    siteHost: 'sp-provenance-site-host',
  },
  // Review queue
  review: {
    root: 'review-root',
    row: (id: string) => `review-row-${id}`,
    empty: 'review-empty',
    conflictBadge: (id: string) => `review-conflict-badge-${id}`,
    practiceAreaBadge: 'review-practice-area-badge',
    noteInput: (id: string) => `review-note-input-${id}`,
    returnButton: (id: string) => `review-return-${id}`,
    sendToReview: 'review-send-to-review',
    signButton: (id: string) => `review-sign-${id}`,
  },
  // Help
  help: {
    root: 'help-root',
    tocTree: 'help-toc',
    tocItem: (topic: string) => `help-toc-${topic.toLowerCase().replace(/\s+/g, '-')}`,
    contentPane: 'help-content',
  },
  // Skills library (Phase D)
  skills: {
    libraryRoot: 'skill-library-root',
    libraryRow: (id: string) => `skill-library-row-${id}`,
    libraryEmpty: 'skill-library-empty',
    // T.1 — Tinker training badge
    libraryRowTrainedBadge: (name: string) => `skill-library-row-trained-${name}`,
    // T.2.e — clickable detail popover surfacing tinkerLoraUrl from
    // the lorasByPortal registry (rendered when the badge is clicked).
    libraryRowTrainedLoraPopover: 'skill-library-row-trained-lora-popover',
    libraryRowTrainedLoraUrl: 'skill-library-row-trained-lora-url',
    // Phase C — popover surfaces the training-run id + a navigation link
    // to Settings → Tinker for the full run history.
    libraryRowTrainedRunId: 'skill-library-row-trained-run-id',
    libraryRowTrainedViewAllLink: 'skill-library-row-trained-view-all',
    libraryRowTrainedRelativeTime: 'skill-library-row-trained-relative-time',
    syncButton: 'skill-library-sync',
    // MK.3 — Skill Marketplace tab inside the library
    libraryTabMine: 'skill-library-tab-mine',
    libraryTabMarketplace: 'skill-library-tab-marketplace',
    marketplaceRoot: 'skill-marketplace-root',
    marketplaceCategoryChip: (id: string) => `skill-marketplace-cat-${id}`,
    marketplaceRow: (slug: string) => `skill-marketplace-row-${slug}`,
    marketplaceInstallButton: (slug: string) => `skill-marketplace-install-${slug}`,
    marketplaceInstalledBadge: (slug: string) => `skill-marketplace-installed-${slug}`,
    marketplaceError: 'skill-marketplace-error',
    // MK.4 — train-a-LoRA button on each library row
    libraryRowTrainButton: (id: string) => `skill-library-train-${id}`,
    libraryRowTrainStatus: (id: string) => `skill-library-train-status-${id}`,
    // L.3 — (?) icon + popover next to the train button
    loraPrimerToggle: (id: string) => `lora-primer-toggle-${id}`,
    loraPrimerPopover: (id: string) => `lora-primer-popover-${id}`,
    loraPrimerOpenNipe: (id: string) => `lora-primer-open-nipe-${id}`,
    // L.6 — multi-select checkboxes for bundle specialization
    libraryRowSelectCheckbox: (id: string) => `skill-library-select-${id}`,
    bundleSpecializeButton: 'skill-library-bundle-specialize',
    bundleSpecializeStatus: 'skill-library-bundle-specialize-status',
    bundleClearButton: 'skill-library-bundle-clear',
    pickerRoot: 'skill-picker-root',
    pickerItem: (id: string) => `skill-picker-item-${id}`,
    pickerSearch: 'skill-picker-search',
    pickerScopeFirm: 'skill-picker-scope-firm',
    pickerScopeClient: 'skill-picker-scope-client',
    saveDialogRoot: 'save-as-skill-dialog-root',
    saveDialogName: 'save-as-skill-name',
    saveDialogDescription: 'save-as-skill-description',
    saveDialogSubmit: 'save-as-skill-submit',
    saveDialogCancel: 'save-as-skill-cancel',
    saveDialogError: 'save-as-skill-error',
    saveDialogCollision: 'save-as-skill-collision',
    skillBodyPreview: 'skill-body-preview',
    skillVersionSelect: 'skill-version-select',
    settingsSync: 'settings-skills-sync',
    settingsSyncStatus: 'settings-skills-sync-status',
    settingsTab: 'settings-tab-skills',
    openInVault: (id: string) => `skill-open-in-vault-${id}`,
  },
  // ──────────────────────────────────────────────────────────────────
  // L.7 — Specialization Lab window
  // ──────────────────────────────────────────────────────────────────
  specializationLab: {
    root: 'specialization-lab-root',
    header: 'specialization-lab-header',
    promptInput: 'specialization-lab-prompt',
    runBothButton: 'specialization-lab-run-both',
    baseColumn: 'specialization-lab-base-column',
    loraColumn: 'specialization-lab-lora-column',
    baseOutput: 'specialization-lab-base-output',
    loraOutput: 'specialization-lab-lora-output',
    baseStatus: 'specialization-lab-base-status',
    loraStatus: 'specialization-lab-lora-status',
    corpusDrawer: 'specialization-lab-corpus-drawer',
    whatChangedCard: 'specialization-lab-what-changed',
    askNipeButton: 'specialization-lab-ask-nipe',
    strengthSlider: 'specialization-lab-strength-slider',
    temperatureSlider: 'specialization-lab-temperature-slider',
    // L.15 — Specialization settings drawer (Tinker hyperparameter exposure).
    settingsDrawer: 'specialization-lab-settings-drawer',
    settingsToggle: 'specialization-lab-settings-toggle',
    settingsBody: 'specialization-lab-settings-body',
    baseModelInput: 'specialization-lab-base-model',
    loraRankSlider: 'specialization-lab-lora-rank-slider',
    loraRankValue: 'specialization-lab-lora-rank-value',
    epochsSlider: 'specialization-lab-epochs-slider',
    epochsValue: 'specialization-lab-epochs-value',
    learningRateSlider: 'specialization-lab-learning-rate-slider',
    learningRateValue: 'specialization-lab-learning-rate-value',
    learningRateAutoToggle: 'specialization-lab-learning-rate-auto',
    costCapSlider: 'specialization-lab-cost-cap-slider',
    costCapValue: 'specialization-lab-cost-cap-value',
    costEstimate: 'specialization-lab-cost-estimate',
    costEstimateApply: 'specialization-lab-cost-estimate-apply',
    costEstimateAutoToggle: 'specialization-lab-cost-estimate-auto',
    datasetScopeOption: (key: string) =>
      `specialization-lab-dataset-scope-${key}`,
    respecializeButton: 'specialization-lab-respecialize',
    respecializeStatus: 'specialization-lab-respecialize-status',
    respecializeError: 'specialization-lab-respecialize-error',
    controlTooltip: (key: string) => `specialization-lab-tooltip-${key}`,
    // L.16 — UI polish + Skills tie-in. Skill picker selects which
    // specialization the Lab is comparing against; probe selector cycles
    // through that skill's curated `probePrompts[]` from the marketplace.
    skillPicker: 'specialization-lab-skill-picker',
    probeGroup: 'specialization-lab-probe-group',
    probeSelect: 'specialization-lab-probe-select',
    probePrev: 'specialization-lab-probe-prev',
    probeNext: 'specialization-lab-probe-next',
    probeIndex: 'specialization-lab-probe-index',
    probeHelpToggle: 'specialization-lab-probe-help-toggle',
    probeHelpPanel: 'specialization-lab-probe-help-panel',
    leversGroup: 'specialization-lab-levers-group',
    outputsGroup: 'specialization-lab-outputs-group',
    leverTooltip: (key: string) => `specialization-lab-lever-tooltip-${key}`,
    changeSpecializationLink: 'specialization-lab-change-specialization',
  },
  // ──────────────────────────────────────────────────────────────────
  // Milestone C — Obsidian Vault + NIPE
  // ──────────────────────────────────────────────────────────────────
  // C.3 — Obsidian Import window. Four import paths (SharePoint folder
  // picker, showDirectoryPicker, drag-and-drop, ZIP upload) plus a
  // shared review/progress screen.
  obsidianImport: {
    root: 'obsidian-import-root',
    pathCard: (kind: 'sharepoint' | 'fsap' | 'dragdrop' | 'zip') =>
      `obsidian-import-path-${kind}`,
    pathButton: (kind: 'sharepoint' | 'fsap' | 'dragdrop' | 'zip') =>
      `obsidian-import-button-${kind}`,
    fsapUnsupported: 'obsidian-import-fsap-unsupported',
    dropZone: 'obsidian-import-drop-zone',
    dropZoneActive: 'obsidian-import-drop-zone-active',
    zipInput: 'obsidian-import-zip-input',
    sharepointPicker: 'obsidian-import-sharepoint-picker',
    // SharePoint folder picker modal — implemented in
    // packages/repancy/src/components/obsidian-import/SharePointFolderPicker.tsx.
    sharePointPickerOverlay: 'obsidian-import-sp-picker-overlay',
    sharePointPickerDialog: 'obsidian-import-sp-picker-dialog',
    sharePointPickerClose: 'obsidian-import-sp-picker-close',
    sharePointPickerDemoBanner: 'obsidian-import-sp-picker-demo-banner',
    sharePointPickerSiteRow: (id: string) =>
      `obsidian-import-sp-picker-site-${id.replace(/[^a-zA-Z0-9-]/g, '_')}`,
    sharePointPickerDriveRow: (id: string) =>
      `obsidian-import-sp-picker-drive-${id.replace(/[^a-zA-Z0-9-]/g, '_')}`,
    sharePointPickerFolderRow: (id: string) =>
      `obsidian-import-sp-picker-folder-${id.replace(/[^a-zA-Z0-9-]/g, '_')}`,
    sharePointPickerBreadcrumbs: 'obsidian-import-sp-picker-breadcrumbs',
    sharePointPickerCancel: 'obsidian-import-sp-picker-cancel',
    sharePointPickerConfirm: 'obsidian-import-sp-picker-confirm',
    helpToggle: 'obsidian-import-help-toggle',
    helpPanel: 'obsidian-import-help-panel',
    progress: 'obsidian-import-progress',
    progressFile: 'obsidian-import-progress-file',
    summary: 'obsidian-import-summary',
    summaryNotesCount: 'obsidian-import-summary-notes',
    summaryFlowsCount: 'obsidian-import-summary-flows',
    summaryAssetsCount: 'obsidian-import-summary-assets',
    summaryErrorsCount: 'obsidian-import-summary-errors',
    cancel: 'obsidian-import-cancel',
    confirm: 'obsidian-import-confirm',
    reset: 'obsidian-import-reset',
    committing: 'obsidian-import-committing',
    committed: 'obsidian-import-committed',
    committedNotesCount: 'obsidian-import-committed-notes',
    committedLinksCount: 'obsidian-import-committed-links',
    committedAssetsCount: 'obsidian-import-committed-assets',
    openVault: 'obsidian-import-open-vault',
    importAnother: 'obsidian-import-another',
  },
  // Obsidian Vault Explorer window.
  obsidianVault: {
    root: 'obsidian-vault-root',
    toolbar: 'obsidian-vault-toolbar',
    treePane: 'obsidian-vault-tree-pane',
    treeNode: (path: string) => `obsidian-vault-tree-node-${path.replace(/[/.\s]/g, '_')}`,
    editorPaneA: 'obsidian-vault-editor-a',
    editorPaneB: 'obsidian-vault-editor-b',
    graphPane: 'obsidian-vault-graph-pane',
    backlinksPane: 'obsidian-vault-backlinks-pane',
    gripperTreeEditor: 'obsidian-vault-gripper-tree-editor',
    gripperEditorSplit: 'obsidian-vault-gripper-editor-split',
    gripperEditorGraph: 'obsidian-vault-gripper-editor-graph',
    saveStatusBadge: 'obsidian-vault-save-status',
    revertToCheckpoint: 'obsidian-vault-revert-checkpoint',
    versionsButton: 'obsidian-vault-versions',
    searchInput: 'obsidian-vault-search',
    searchResult: (path: string) => `obsidian-vault-search-result-${path.replace(/[/.\s]/g, '_')}`,
    newNoteButton: 'obsidian-vault-new-note',
    editToggle: 'obsidian-vault-edit-toggle',
    importVaultButton: 'obsidian-vault-import-button',
    registerSkillBanner: 'obsidian-vault-register-skill-banner',
    registerSkillButton: 'obsidian-vault-register-skill-button',
    registerSkillDialog: 'obsidian-vault-register-skill-dialog',
    registerSkillCancel: 'obsidian-vault-register-skill-cancel',
    registerSkillSubmit: 'obsidian-vault-register-skill-submit',
    // 2026-05-28 — header surface for vault identity + multi-vault
    // selection. titleLabel is the live name (or "Obsidian Vault"
    // for the sample fallback). sourcePill renders the imported vs
    // sample badge. picker is rendered only when listVaults returns
    // ≥2 rows for the portal.
    titleLabel: 'obsidian-vault-title-label',
    sourcePill: 'obsidian-vault-source-pill',
    vaultPicker: 'obsidian-vault-picker',
    // F7 — vault export menu.
    exportMenuButton: 'obsidian-vault-export-button',
    exportMenuPanel: 'obsidian-vault-export-panel',
    exportFolder: 'obsidian-vault-export-folder',
    exportZip: 'obsidian-vault-export-zip',
    exportSharePoint: 'obsidian-vault-export-sharepoint',
    // B.5 — NIPE-authored draft badge (rendered when frontmatter.source === 'nipe').
    nipeDraftBadge: (path: string) =>
      `obsidian-vault-nipe-draft-badge-${path.replace(/[/.\s]/g, '_')}`,
  },
  // C.7 — Vault Graph (popout).
  vaultGraph: {
    root: 'vault-graph-root',
    canvas: 'vault-graph-canvas',
    layoutToggle: 'vault-graph-layout-toggle',
    tagFilter: 'vault-graph-tag-filter',
    folderFilter: 'vault-graph-folder-filter',
    node: (path: string) => `vault-graph-node-${path.replace(/[/.\s]/g, '_')}`,
    nodeContextMenu: 'vault-graph-node-context-menu',
  },
  // C.13a — inline Dataview blocks (TipTap node view).
  dataview: {
    block: (pos: number) => `dataview-block-${pos}`,
    table: 'dataview-table',
    headerCell: (field: string) => `dataview-header-${field.replace(/\./g, '_')}`,
    errorBanner: 'dataview-error',
    loadingIndicator: 'dataview-loading',
  },
  // C.13c — .flow editor (XYFlow surface).
  flow: {
    root: 'flow-root',
    canvas: 'flow-canvas',
    node: (id: string) => `flow-node-${id}`,
    edge: (id: string) => `flow-edge-${id}`,
    nodePalette: 'flow-node-palette',
    paletteItem: (kind: string) => `flow-palette-item-${kind}`,
    popoutButton: 'flow-popout',
    addNodeButton: 'flow-add-node',
    removeNodeButton: (id: string) => `flow-remove-node-${id}`,
    relabelDialog: 'flow-relabel-dialog',
  },
  // C.13b — Bases window.
  bases: {
    root: 'bases-root',
    leftRail: 'bases-left-rail',
    viewItem: (id: string) => `bases-view-${id}`,
    viewBuilder: 'bases-view-builder',
    viewBuilderName: 'bases-view-builder-name',
    viewBuilderFromTag: 'bases-view-builder-from-tag',
    viewBuilderFromFolder: 'bases-view-builder-from-folder',
    viewBuilderWhereRow: (i: number) => `bases-view-builder-where-row-${i}`,
    viewBuilderColumnPicker: 'bases-view-builder-columns',
    viewBuilderKindToggle: 'bases-view-builder-kind',
    table: 'bases-table',
    addView: 'bases-add-view',
    deleteView: (id: string) => `bases-delete-view-${id}`,
    renameView: (id: string) => `bases-rename-view-${id}`,
    exportAsDataview: 'bases-export-as-dataview',
    exportPositionRadio: (p: 'top' | 'cursor' | 'bottom') => `bases-export-pos-${p}`,
  },
  // C.10–C.12 — NIPE overlay.
  nipe: {
    root: 'nipe-root',
    sprite: 'nipe-sprite',
    bubble: 'nipe-bubble',
    /** 5000ms first-load greet bubble (reuses NipeIntroBalloon styling). */
    greetBubble: 'nipe-greet-bubble',
    bubbleDismiss: 'nipe-bubble-dismiss',
    chatPanel: 'nipe-chat-panel',
    chatInput: 'nipe-chat-input',
    chatSend: 'nipe-chat-send',
    chatStop: 'nipe-chat-stop',
    chatMessage: (id: string) => `nipe-chat-message-${id}`,
    chatStreaming: 'nipe-chat-streaming',
    // F3 — pending SP-write approval tray.
    pendingWritesTrayButton: 'nipe-pending-writes-tray-button',
    pendingWritesTrayBadge: 'nipe-pending-writes-tray-badge',
    pendingWritesTrayPanel: 'nipe-pending-writes-tray-panel',
    pendingWriteRow: (id: string) => `nipe-pending-write-row-${id}`,
    pendingWritePreview: (id: string) => `nipe-pending-write-preview-${id}`,
    pendingWriteApprove: (id: string) => `nipe-pending-write-approve-${id}`,
    pendingWriteReject: (id: string) => `nipe-pending-write-reject-${id}`,
    pendingWriteStatus: (id: string) => `nipe-pending-write-status-${id}`,
    // H.2 — auto-open opt-out checkbox in the tray panel.
    pendingWritesAutoOpenToggle: 'nipe-pending-writes-auto-open-toggle',
    // F5 — folder-drop dialog.
    portableSiteDropDialog: 'nipe-portable-site-drop-dialog',
    portableSiteDropAsVault: 'nipe-portable-site-drop-as-vault',
    portableSiteDropAsSharePoint: 'nipe-portable-site-drop-as-sharepoint',
    portableSiteDropAsBoth: 'nipe-portable-site-drop-as-both',
    portableSiteDropProfile: (p: string) => `nipe-portable-site-drop-profile-${p}`,
    portableSiteDropCancel: 'nipe-portable-site-drop-cancel',
    portableSiteDropIssuesPanel: 'nipe-portable-site-drop-issues',
    // G.2 — verify-portable-site round-trip affordance
    portableSiteDropVerifyButton: 'nipe-portable-site-drop-verify',
    portableSiteDropVerifyClean: 'nipe-portable-site-drop-verify-clean',
    portableSiteDropVerifyDiff: 'nipe-portable-site-drop-verify-diff',
    // D.3.5 — inline SharePoint provisioning panel mounted by the drop dialog.
    vaultDropProvisionPanel: 'nipe-vault-drop-provision-panel',
    vaultDropProvisionTitle: 'nipe-vault-drop-provision-title',
    vaultDropProvisionStart: 'nipe-vault-drop-provision-start',
    vaultDropProvisionCancel: 'nipe-vault-drop-provision-cancel',
    vaultDropProvisionProgress: 'nipe-vault-drop-provision-progress',
    vaultDropProvisionResultUrl: 'nipe-vault-drop-provision-result-url',
    vaultDropProvisionError: 'nipe-vault-drop-provision-error',
    vaultDropProvisionDemoBadge: 'nipe-vault-drop-provision-demo-badge',
    vaultDropProvisionRetry: 'nipe-vault-drop-provision-retry',
    topicTile: (key: string) => `nipe-topic-${key}`,
    topicTileGrid: 'nipe-topic-grid',
    trayIcon: 'nipe-tray-icon',
    hideButton: 'nipe-hide-button',
    showButton: 'nipe-show-button',
    firstVisitOverlay: 'nipe-first-visit-overlay',
    firstVisitDismiss: 'nipe-first-visit-dismiss',
    // Phase 6 — first-run guided spotlight tour.
    tourRoot: 'first-run-tour',
    tourBackdrop: 'first-run-tour-backdrop',
    tourCutout: 'first-run-tour-cutout',
    tourBubble: 'first-run-tour-bubble',
    tourBubbleStep: (i: number) => `first-run-tour-step-${i}`,
    tourNext: 'first-run-tour-next',
    tourSkip: 'first-run-tour-skip',
    tourCta: 'first-run-tour-cta',
    // "Take the tour" Start Menu item. Name kept compatible with the
    // original pillar-relaunch flow it replaced; the pillar sequence
    // itself was retired and deleted alongside the attention
    // orchestrator landing.
    tourRelaunch: 'nipe-tour-relaunch',
    // Phase F (Q3d / F.6) — stuck-recovery bubbles.
    stuckBubbles: 'nipe-stuck-bubbles',
    stuckBubble: (idx: number) => `nipe-stuck-bubble-${idx}`,
    stuckBubbleDismiss: 'nipe-stuck-bubble-dismiss',
    stuckBubbleSuggestion: (idx: number) => `nipe-stuck-suggestion-${idx}`,
    stuckMenuItem: 'nipe-stuck-menu-item',
  },
  // Phase F (C-3) — styled tooltip primitive.
  tooltip: {
    bubble: 'ui-tooltip-bubble',
  },
  // Phase H (H.2) — version history dialog.
  versionHistory: {
    root: 'version-history-root',
    overlay: 'version-history-overlay',
    list: 'version-history-list',
    row: (n: number) => `version-history-row-${n}`,
    toggle: (n: number) => `version-history-toggle-${n}`,
    diff: (n: number) => `version-history-diff-${n}`,
    diffLine: (type: string) => `version-history-diff-line-${type}`,
    emptyState: 'version-history-empty',
    closeButton: 'version-history-close',
  },
  // Phase H (H.3) — per-document preview window.
  docWindow: {
    root: (id: string) => `doc-window-${id}`,
  },
  // Phase H (H.1) — Send-to-Prompt-Lab suggestion chips on prompt lab editor.
  promptLabSuggestions: {
    root: 'prompt-lab-suggestions-root',
    chip: (idx: number) => `prompt-lab-suggestion-${idx}`,
  },
  // F18 — Customer-facing portal provisioning wizard (NIPE-augmented self-serve).
  onboard: {
    routeRoot: 'onboard-route-root',
    stepHeader: 'onboard-step-header',
    introStep: 'onboard-intro-step',
    tenantStep: 'onboard-tenant-step',
    consentStep: 'onboard-consent-step',
    signinStep: 'onboard-signin-step',
    firmStep: 'onboard-firm-step',
    progressStep: 'onboard-progress-step',
    completedStep: 'onboard-completed-step',
    nipeBalloon: 'onboard-nipe-balloon',
    // XP-style welcome overlay played on "Open my portal" click.
    welcomeOverlay: 'onboard-welcome-overlay',
    welcomeOverlayText: 'onboard-welcome-overlay-text',
  },
  // L.12 — /learn/lora marketing page
  learnLora: {
    root: 'learn-lora-root',
    title: 'learn-lora-title',
    body: 'learn-lora-body',
    faq: 'learn-lora-faq',
    faqEntry: (q: string) => `learn-lora-faq-${q.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40)}`,
    ctaButton: 'learn-lora-cta',
  },
  // F-BULK-IMPORT (2026-05-24) — bulk-import wizard + progress window
  bulkImport: {
    wizard: 'bulk-import-wizard',
    wizardOverlay: 'bulk-import-wizard-overlay',
    stepHeader: 'bulk-import-step-header',
    scopeStep: 'bulk-import-scope-step',
    mappingStep: 'bulk-import-mapping-step',
    optionsStep: 'bulk-import-options-step',
    reviewStep: 'bulk-import-review-step',
    samplePreview: 'bulk-import-sample-preview',
    sampleRow: (idx: number) => `bulk-import-sample-row-${idx}`,
    scopeKindRadio: (kind: 'site' | 'drive' | 'folder' | 'search') =>
      `bulk-import-scope-${kind}`,
    mappingPolicyRadio: (policy: 'single-matter' | 'folder-per-matter') =>
      `bulk-import-mapping-${policy}`,
    linkModeRadio: (mode: 'link' | 'copy') => `bulk-import-link-${mode}`,
    dryRunCheckbox: 'bulk-import-dry-run',
    kickButton: 'bulk-import-kick',
    cancelButton: 'bulk-import-cancel',
    nextButton: 'bulk-import-next',
    backButton: 'bulk-import-back',
    siteRow: (siteId: string) => `bulk-import-site-${siteId}`,
    driveRow: (driveId: string) => `bulk-import-drive-${driveId}`,
    folderRow: (folderId: string) => `bulk-import-folder-${folderId}`,
    progressWindow: 'bulk-import-progress-window',
    progressJobRow: (jobId: string) => `bulk-import-job-row-${jobId}`,
    progressJobStatus: (jobId: string) => `bulk-import-job-status-${jobId}`,
    progressJobCounters: (jobId: string) => `bulk-import-job-counters-${jobId}`,
    progressJobBar: (jobId: string) => `bulk-import-job-bar-${jobId}`,
    progressJobETA: (jobId: string) => `bulk-import-job-eta-${jobId}`,
    pauseButton: (jobId: string) => `bulk-import-pause-${jobId}`,
    resumeButton: (jobId: string) => `bulk-import-resume-${jobId}`,
    cancelJobButton: (jobId: string) => `bulk-import-cancel-job-${jobId}`,
    exportFailedCsvButton: (jobId: string) =>
      `bulk-import-export-failed-${jobId}`,
    matterPicker: 'bulk-import-matter-picker',
    matterOption: (matterId: string) => `bulk-import-matter-option-${matterId}`,
  },
} as const;
